import { type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getSupportEmailAddress } from 'Cohorts';
import { MailtoLink } from 'MailtoLink';
import { Modal } from 'Modal';
import { Text } from 'Text';
import { type CurrentUser, getCohort } from 'Users';
import { LOCALE_PREFIX } from '../constants';

type BankDetailsModalProps = {
    currentUser: CurrentUser;
    // A single tuition plan gets shorter intro copy.
    singlePaymentSituation: boolean;
    onClose: () => void;
};

/*
    The bank details stay hardcoded and untranslated: they must match the account exactly, and Quantic
    Holdings Inc holds it for every brand.
*/
export function BankDetailsModal({
    currentUser,
    singlePaymentSituation,
    onClose,
}: BankDetailsModalProps): ReactElement {
    const { t } = useTranslation('back_royal');
    const cohort = getCohort(currentUser);
    const billingEmail = cohort ? (getSupportEmailAddress(cohort, 'billing') ?? '') : '';

    const rows: { heading: string; value: ReactElement }[] = [
        {
            heading: t(`${LOCALE_PREFIX}.our_bank_heading`),
            value: (
                <>
                    Bridge Bank, a Division of Western Alliance
                    <br />
                    55 Almaden Blvd, San Jose, CA 95113
                    <br />
                    USA
                </>
            ),
        },
        {
            heading: t(`${LOCALE_PREFIX}.our_account_name_heading`),
            value: <>Quantic Holdings Inc dba Quantic School of Business and Technology</>,
        },
        { heading: t(`${LOCALE_PREFIX}.our_account_number_heading`), value: <>8593474229</> },
        {
            heading: t(`${LOCALE_PREFIX}.for_us_wires_heading`),
            value: <>{t(`${LOCALE_PREFIX}.for_us_wires_content`, { transitNumber: '121143260' })}</>,
        },
        { heading: t(`${LOCALE_PREFIX}.for_international_wires_heading`), value: <>SWIFT ID: BBFXUS6S</> },
        {
            heading: t(`${LOCALE_PREFIX}.quantic_mailing_address_heading`, { institutionName: 'Quantic' }),
            value: (
                <>
                    712 H Street NE Suite 1802
                    <br />
                    Washington, DC 20002
                    <br />
                    USA
                </>
            ),
        },
    ];

    return (
        <Modal titleKey={`${LOCALE_PREFIX}.wiring_funds`} size="lg" onClose={onClose}>
            <div className="flex flex-col gap-4 px-7.5 pb-7.5">
                <Text as="p" className="mb-0">
                    {t(`${LOCALE_PREFIX}.bank_details_intro${singlePaymentSituation ? '_1_pay_only' : ''}`)}
                </Text>

                <dl className="m-0 flex flex-col gap-3">
                    {rows.map(row => (
                        <div key={row.heading}>
                            <dt className="text-xs font-semibold uppercase text-slate-grey">{row.heading}</dt>
                            <dd className="m-0 text-sm text-black">{row.value}</dd>
                        </div>
                    ))}
                </dl>

                <Text as="p" className="mb-0">
                    {t(`${LOCALE_PREFIX}.identifier`)}
                </Text>

                <Text as="p" className="mb-0">
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.contact_billing_with_questions`}
                        components={{ mail: <MailtoLink className="hover:text-primary" email={billingEmail} /> }}
                    />
                </Text>
            </div>
        </Modal>
    );
}
